import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { getVerificationSetupStatus } from '@/apis/experience/verification-setup';
import useApi from '@/hooks/use-api';
import useErrorHandler from '@/hooks/use-error-handler';
import type { SetupStatusResponse } from '@/pages/MfaCompleteSetup/types';

const useSetupGuard = () => {
  const navigate = useNavigate();
  const handleError = useErrorHandler();
  const [searchParams] = useSearchParams();

  const [setupStatus, setSetupStatus] = useState<SetupStatusResponse | undefined>(null);
  const [isLoading, setIsLoading] = useState(true);

  const asyncGetSetupStatus = useApi(getVerificationSetupStatus);
  
  // Get userId from URL parameters if available
  const userId = searchParams.get('user_id') || undefined;

  // Check setup status
  const checkSetupStatus = useCallback(async () => {
    try {
      setIsLoading(true);

      const [error, result] = await asyncGetSetupStatus(userId);

      if (error) {
        // Check if it's a session error - redirect to start fresh
        if (error instanceof Error && (
          error.message?.includes('session not found') || 
          error.message?.includes('Session not found') ||
          error.message?.includes('interaction session id cookie not found')
        )) {
          console.error('Session expired during setup status check! Redirecting to error page...');
          window.location.href = '/error?message=session_expired';
          return;
        }
        
        // If we can't check setup status for other reasons, don't block the user
        // This ensures the feature doesn't break existing functionality
        console.warn('Setup status check failed:', error);
        setSetupStatus({
          setupRequired: false,
          setupCompleted: true,
        });
        return;
      }

      if (result) {
        setSetupStatus(result);
      }
    } catch (error) {
      console.warn('Setup status check error:', error);
      // Default to allowing access if check fails
      setSetupStatus({
        setupRequired: false,
        setupCompleted: true,
      });
    } finally {
      setIsLoading(false);
    }
  }, [asyncGetSetupStatus, userId]);

  // Redirect to setup if required
  const enforceSetupRequirement = useCallback(
    (currentPath?: string) => {
      if (!setupStatus || isLoading) {
        return;
      }

      // Skip enforcement for setup-related routes
      const isSetupRoute = currentPath?.includes('/verification-setup');
      const isSignOutRoute = currentPath?.includes('/sign-out');

      if (isSetupRoute || isSignOutRoute) {
        return;
      }

      // If setup is required and not completed, redirect to setup
      if (setupStatus.setupRequired && !setupStatus.setupCompleted) {
        const redirectUrl = userId ? `/verification-setup?user_id=${userId}` : '/verification-setup';
        navigate(redirectUrl, { replace: true });
      }
    },
    [setupStatus, isLoading, navigate, userId]
  );

  // Check setup status on mount
  useEffect(() => {
    // Skip setup status check for verification routes to avoid race conditions
    const isVerificationRoute = window.location.pathname.includes('/verification-');
    
    if (!isVerificationRoute && userId) {
      void checkSetupStatus();
    } else {
      // For verification routes, default to allowing access
      setSetupStatus({
        setupRequired: false,
        setupCompleted: true,
      });
      setIsLoading(false);
    }
  }, [checkSetupStatus, userId]);

  return {
    setupStatus,
    isLoading,
    checkSetupStatus,
    enforceSetupRequirement,

    // Helper properties
    setupRequired: setupStatus?.setupRequired ?? false,
    setupCompleted: setupStatus?.setupCompleted ?? true,
    isSetupInProgress: setupStatus?.setupRequired && !setupStatus.setupCompleted,
  };
};

export default useSetupGuard;
